// Inbox's views (pages/ux-full components/inbox.js): what waits for an OK and what the agents did,
// thin views over RowList (BL-189 N1; their rows are rows/inbox-rows.ts). Home and a chat card draw
// them the same way. One request is the To approve page's (apps/web inbox/InboxPages.tsx) and the
// notifications are NoteList's (components/NoteList). A chat never decides (DEC-009: a tap in a card
// is an ordinary tool call), it opens Flowology.
import type { AgentActivityProps, ApprovalListProps } from '../schemas.ts';
import type { ViewProps } from '../host.ts';
import { day, plural } from '../format.ts';
import { RowList } from './RowList/RowList.tsx';
import { ViewAct } from './view-act.tsx';
import { activityRows, approvalRows } from '../rows/inbox-rows.ts';

/**
 * approvals.list: what waits for the person's OK, one request a row (rows/inbox-rows.ts), drawn by
 * RowList: who asked (an agent or the person's own AI tool), what it would do, when and why; at its end
 * whether it waits for them or for someone else. Five, then Show all (in a chat four, then how many
 * more), then one way in: Decide them. In a chat an approval is never decided (DEC-009: a tap in a card
 * is an ordinary tool call), only opened in Flowology.
 */
export function ApprovalList({ data, host }: ViewProps<ApprovalListProps>) {
  const chat = host.surface === 'chat';
  const waiting = data.approvals.filter((a) => a.status === 'pending');
  // yours are those you may decide; the rest wait on someone else (QA-860: the owner's, for money)
  const yours = waiting.filter((a) => !a.cannotDecide).length;
  const others = waiting.length - yours;
  const shown = chat ? waiting.slice(0, 4) : waiting;
  return (
    <RowList
      rows={approvalRows(shown, { ends: !chat })}
      label={`${yours} waiting for your OK`}
      title={
        yours ? `${plural(yours, 'thing waits', 'things wait')} for your OK` : 'Nothing waits for your OK'
      }
      {...(others > 0
        ? { sub: `${plural(others, 'request waits', 'requests wait')} for someone else’s OK.` }
        : {})}
      {...(chat ? {} : { first: 5 })}
      empty="Nothing waits for your OK."
      {...(waiting.length > shown.length ? { note: `And ${waiting.length - shown.length} more.` } : {})}
      act={
        <ViewAct surface={host.surface} primary onPress={() => host.open({ kind: 'approvals' })}>
          {chat ? 'Open in Flowology to approve' : 'Decide them'}
        </ViewAct>
      }
    />
  );
}

/**
 * agents.activity: what the agents did today, one step a row (rows/inbox-rows.ts), drawn by RowList:
 * what it did, which agent, when and how it went; at its end how it was decided, or Done, Waiting for
 * you, Refused or Failed. Five, then Show all (in a chat four, then how many more); with a step still
 * waiting, one way in: Decide what waits. A page whose section already names it (Home's What the
 * agents did today) passes titled={false}.
 */
export function AgentActivity({
  data,
  host,
  titled = true,
}: ViewProps<AgentActivityProps> & {
  /** Its own title, What the agents did; false where the section over it already says so. */
  titled?: boolean;
}) {
  const chat = host.surface === 'chat';
  const shown = chat ? data.steps.slice(0, 4) : data.steps;
  // asked and still undecided: one a person already decided waits for nobody (QA-858)
  const waiting = data.steps.filter((s) => s.mode === 'asked' && !s.decision).length;
  return (
    <RowList
      rows={activityRows(shown)}
      label="What the agents did"
      {...(titled ? { title: 'What the agents did' } : {})}
      sub={`${plural(data.steps.length, 'step')}${waiting ? `, ${waiting} waiting for you` : ''}`}
      {...(chat ? {} : { first: 5 })}
      empty={`The agents haven’t done anything ${data.date ? `on ${day(data.date)}` : 'today'}.`}
      {...(data.steps.length > shown.length ? { note: `And ${data.steps.length - shown.length} more.` } : {})}
      {...(waiting > 0
        ? {
            act: (
              <ViewAct
                surface={host.surface}
                primary={!chat}
                onPress={() => host.open({ kind: 'approvals' })}
              >
                {chat ? 'Open in Flowology to approve' : 'Decide what waits'}
              </ViewAct>
            ),
          }
        : {})}
    />
  );
}
